import { useEffect } from "react";
import { useAdminSettingsBatch, type SettingsMap } from "@/hooks/useAdminSettingsBatch";

/** admin_settings key holding the logo used on LIGHT backgrounds (dark artwork). */
export const BRAND_LOGO_KEY = "branding_logo_url";
/** admin_settings key holding the logo used on DARK backgrounds (light artwork). */
export const BRAND_LOGO_DARK_KEY = "branding_logo_dark_url";

/** Last-known logos, read synchronously on first paint so the old wordmark never flashes. */
const CACHE_KEY = "podgo_brand_logo_cache";

type Logos = { light: string; dark: string };

function readCache(): Logos | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Logos;
    return typeof parsed?.light === "string" && typeof parsed?.dark === "string" ? parsed : null;
  } catch {
    return null;
  }
}

function pick(map: SettingsMap): Logos {
  const light = (map[BRAND_LOGO_KEY] ?? "").trim();
  const dark = (map[BRAND_LOGO_DARK_KEY] ?? "").trim();
  return {
    /** Logo for light backgrounds, falling back to the dark-background one. */
    light: light || dark,
    /** Logo for dark backgrounds, falling back to the light-background one. */
    dark: dark || light,
  };
}

/**
 * Site-wide PODGO logos configured in Theme Options -> Branding. Either slot may
 * be empty, in which case the other one is reused; when both are empty call
 * sites fall back to the built-in wordmark. Served from the shared batched
 * settings read; a display-only cache covers the first frames (the database
 * stays the source of truth and overwrites it on every load).
 */
export function useBrandLogo() {
  const query = useAdminSettingsBatch(pick);
  const cached = query.data ? null : readCache();

  useEffect(() => {
    if (!query.data) return;
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(query.data));
    } catch {
      /* storage unavailable — cache is optional */
    }
  }, [query.data]);

  return {
    ...query,
    data: query.data ?? cached ?? { light: "", dark: "" },
    /** True while we don't yet know which logo to show (no data, no cache). */
    pending: !query.data && !cached && query.isLoading,
  };
}
